import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios';
import { useAuthStore } from '@/stores/authStore';
import { ROUTES } from '@/lib/constants';
import type { TokensInfo } from './types/auth';

const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor: attach Bearer token
apiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = useAuthStore.getState().getAccessToken();
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Request interceptor: attach session replay ID
// (traceparent is now auto-propagated by OTel fetch/XHR instrumentation)
apiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  if (config.headers) {
    const sessionId = window.__endoSessionId;
    if (sessionId) {
      config.headers['X-Session-Id'] = sessionId;
    }
  }
  return config;
});

// Response interceptor: handle 401 with silent refresh
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (error: unknown) => void;
}> = [];

function processQueue(error: unknown, token: string | null) {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else if (token) {
      resolve(token);
    } else {
      reject(new Error('Refresh produced no token'));
    }
  });
  failedQueue = [];
}

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config;
    if (!originalRequest) return Promise.reject(error);

    const isRetry = (originalRequest as InternalAxiosRequestConfig & { _retry?: boolean })._retry;

    // Don't retry refresh-token calls, non-401 errors, or already-retried requests
    if (error.response?.status !== 401 || originalRequest.url === '/auth/refresh-token' || isRetry) {
      return Promise.reject(error);
    }

    (originalRequest as InternalAxiosRequestConfig & { _retry?: boolean })._retry = true;

    if (isRefreshing) {
      return new Promise<string>((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      }).then((token) => {
        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${token}`;
        }
        return apiClient(originalRequest);
      });
    }

    isRefreshing = true;
    const store = useAuthStore.getState();
    const refreshToken = store.getRefreshToken();
    const currentAccessToken = store.getAccessToken();

    if (!refreshToken) {
      store.logout();
      window.location.href = ROUTES.LOGIN;
      return Promise.reject(error);
    }

    try {
      const { data } = await apiClient.post<TokensInfo>('/auth/refresh-token', {
        accessToken: currentAccessToken ?? '',
        refreshToken,
      });

      if (data.accessToken && data.refreshToken) {
        store.setTokens(data.accessToken, data.refreshToken);
        processQueue(null, data.accessToken);

        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${data.accessToken}`;
        }
        return apiClient(originalRequest);
      }

      throw new Error('Refresh failed');
    } catch (refreshError) {
      processQueue(refreshError, null);
      store.logout();
      window.location.href = ROUTES.LOGIN;
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  },
);

export default apiClient;
